import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Scales, Clock, Buildings, Calculator, Download, WarningOctagon, TrendUp,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatINR, formatDate } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { cn as classJoin } from "../lib/utils";

const BUCKET_META = [
    { key: "current", label: "Not due yet", hint: "Due date has not passed", tone: "bg-slate-50 text-slate-800 border-slate-200" },
    { key: "0_30", label: "1 – 30 days", hint: "Overdue 1 to 30 days", tone: "bg-amber-50 text-amber-800 border-amber-200" },
    { key: "31_60", label: "31 – 60 days", hint: "Overdue 31 to 60 days", tone: "bg-orange-50 text-orange-800 border-orange-200" },
    { key: "61_90", label: "61 – 90 days", hint: "Overdue 61 to 90 days", tone: "bg-rose-50 text-rose-800 border-rose-200" },
    { key: "over_90", label: "90+ days", hint: "Overdue more than 90 days", tone: "bg-rose-100 text-rose-900 border-rose-300" },
];

function csvEscape(v) {
    const s = String(v ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n")
        ? `"${s.replace(/"/g, '""')}"`
        : s;
}

function downloadCsv(filename, rows) {
    const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export default function AgingReport() {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [asOf, setAsOf] = useState("");  // blank → today on backend
    const [q, setQ] = useState("");

    const load = async (dateStr) => {
        setLoading(true);
        try {
            const url = dateStr
                ? `/billing/reports/aging?as_of=${encodeURIComponent(dateStr)}`
                : "/billing/reports/aging";
            const { data } = await api.get(url);
            setReport(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load aging report"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(""); }, []);

    const runAsOf = () => {
        if (!asOf) { load(""); return; }
        const iso = new Date(`${asOf}T23:59:59Z`).toISOString();
        load(iso);
    };

    const filteredByClient = useMemo(() => {
        if (!report) return [];
        if (!q.trim()) return report.by_client;
        const n = q.trim().toLowerCase();
        return report.by_client.filter((r) =>
            r.client_name.toLowerCase().includes(n)
            || (r.state_code || "").toLowerCase().includes(n));
    }, [report, q]);

    const filteredOverdue = useMemo(() => {
        if (!report) return [];
        if (!q.trim()) return report.overdue_invoices;
        const n = q.trim().toLowerCase();
        return report.overdue_invoices.filter((r) =>
            r.client_name.toLowerCase().includes(n)
            || r.invoice_number.toLowerCase().includes(n)
            || (r.bucket || "").toLowerCase().includes(n));
    }, [report, q]);

    const exportByClient = () => {
        if (!report) return;
        const header = ["Client", "State", "Count", "Current", "0-30", "31-60", "61-90", "90+", "Outstanding", "Interest (18% p.a.)"];
        const rows = [header, ...report.by_client.map((r) => [
            r.client_name, r.state_code, r.count,
            r.current, r["0_30"], r["31_60"], r["61_90"], r.over_90,
            r.outstanding, r.interest_accrued,
        ])];
        downloadCsv(`aging_by_client_${(report.as_of || "").slice(0, 10)}.csv`, rows);
    };

    const exportOverdue = () => {
        if (!report) return;
        const header = ["Invoice", "Client", "State", "Invoice date", "Due date", "Days overdue", "Bucket", "Total", "Paid", "Credits", "Outstanding", "Interest"];
        const rows = [header, ...report.overdue_invoices.map((r) => [
            r.invoice_number, r.client_name, r.state_code,
            (r.invoice_date || "").slice(0, 10),
            (r.due_date || "").slice(0, 10),
            r.days_overdue, r.bucket,
            r.total_amount, r.paid_amount, r.credit_note_total,
            r.outstanding, r.interest_accrued,
        ])];
        downloadCsv(`overdue_invoices_${(report.as_of || "").slice(0, 10)}.csv`, rows);
    };

    return (
        <div data-testid="aging-report-page">
            <PageHeader
                eyebrow="MIS · Collections"
                title="Aging Report"
                description="Outstanding invoices bucketed by overdue days, with 18% p.a. simple interest accrual per master agreement clause 2.4."
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5">
                            <Input
                                type="date"
                                value={asOf}
                                onChange={(e) => setAsOf(e.target.value)}
                                className="w-36"
                                data-testid="aging-asof-input"
                            />
                            <Button variant="outline" size="sm" onClick={runAsOf} data-testid="aging-run-btn" className="gap-1">
                                As of
                            </Button>
                        </div>
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                {loading ? (
                    <div className="text-sm text-slate-500">Loading report…</div>
                ) : !report ? (
                    <div className="text-sm text-slate-500">No data.</div>
                ) : (
                    <>
                        {/* Top KPIs */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="aging-kpis">
                            <div className="reedos-card p-4">
                                <div className="reedos-eyebrow inline-flex items-center gap-1.5"><Scales size={12} /> Total outstanding</div>
                                <div className="font-display font-bold reedos-data text-2xl mt-1 text-slate-900" data-testid="aging-total-outstanding">
                                    {formatINR(report.totals.outstanding)}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5">Across {report.totals.clients_with_outstanding} client{report.totals.clients_with_outstanding !== 1 ? "s" : ""}</div>
                            </div>
                            <div className="reedos-card p-4">
                                <div className="reedos-eyebrow inline-flex items-center gap-1.5"><Clock size={12} /> Overdue invoices</div>
                                <div className="font-display font-bold reedos-data text-2xl mt-1 text-rose-700">
                                    {report.totals.overdue_invoice_count}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5">Past due date</div>
                            </div>
                            <div className="reedos-card p-4">
                                <div className="reedos-eyebrow inline-flex items-center gap-1.5"><Calculator size={12} /> Interest accrued</div>
                                <div className="font-display font-bold reedos-data text-2xl mt-1 text-amber-700" data-testid="aging-total-interest">
                                    {formatINR(report.totals.interest_accrued)}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5">@ {(report.interest_rate_apa * 100).toFixed(0)}% p.a.</div>
                            </div>
                            <div className="reedos-card p-4">
                                <div className="reedos-eyebrow inline-flex items-center gap-1.5"><TrendUp size={12} /> Settle amount</div>
                                <div className="font-display font-bold reedos-data text-2xl mt-1 text-rose-700" data-testid="aging-settle-amount">
                                    {formatINR(report.totals.settle_amount)}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5">Outstanding + interest</div>
                            </div>
                        </div>

                        {/* Bucket strip */}
                        <div className="reedos-card overflow-hidden" data-testid="aging-buckets">
                            <div className="px-5 py-4 border-b border-slate-200">
                                <div className="reedos-eyebrow">Aging buckets</div>
                                <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">
                                    As of {formatDate(report.as_of)}
                                </h3>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-5 divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
                                {BUCKET_META.map(({ key, label, hint, tone }) => {
                                    const b = report.buckets[key] || { count: 0, amount: 0, interest: 0 };
                                    return (
                                        <div key={key} className="px-5 py-4" data-testid={`bucket-${key}`}>
                                            <div className="flex items-center justify-between">
                                                <span className={classJoin("inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold border", tone)}>
                                                    {label}
                                                </span>
                                                <span className="text-[11px] text-slate-500">{b.count}</span>
                                            </div>
                                            <div className="mt-2 reedos-data font-display font-bold text-xl text-slate-900">
                                                {formatINR(b.amount)}
                                            </div>
                                            <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>
                                            {b.interest > 0 && (
                                                <div className="text-[11px] text-amber-700 mt-1">
                                                    + {formatINR(b.interest)} interest
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Filter + actions */}
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Filter by client, state, invoice number or bucket…"
                                className="max-w-md"
                                data-testid="aging-filter-input"
                            />
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" onClick={exportByClient} className="gap-1.5" data-testid="aging-export-by-client">
                                    <Download size={14} /> Export by-client CSV
                                </Button>
                                <Button size="sm" variant="outline" onClick={exportOverdue} className="gap-1.5" data-testid="aging-export-overdue">
                                    <Download size={14} /> Export overdue CSV
                                </Button>
                            </div>
                        </div>

                        {/* By-client table */}
                        <div className="reedos-card">
                            <div className="px-5 py-4 border-b border-slate-200">
                                <div className="reedos-eyebrow">By client</div>
                                <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">
                                    {filteredByClient.length} client{filteredByClient.length !== 1 ? "s" : ""} with outstanding
                                </h3>
                            </div>
                            {filteredByClient.length === 0 ? (
                                <EmptyState icon={<Buildings size={36} weight="thin" />} title="Nothing outstanding" description="All invoices are settled or no data matches the filter." />
                            ) : (
                                <table className="w-full text-sm" data-testid="aging-by-client-table">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr className="text-left">
                                            <th className="px-5 py-3 reedos-eyebrow">Client</th>
                                            <th className="px-5 py-3 reedos-eyebrow text-right">Not due</th>
                                            <th className="px-5 py-3 reedos-eyebrow text-right">1-30</th>
                                            <th className="px-5 py-3 reedos-eyebrow text-right">31-60</th>
                                            <th className="px-5 py-3 reedos-eyebrow text-right">61-90</th>
                                            <th className="px-5 py-3 reedos-eyebrow text-right">90+</th>
                                            <th className="px-5 py-3 reedos-eyebrow text-right">Outstanding</th>
                                            <th className="px-5 py-3 reedos-eyebrow text-right">Interest</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredByClient.map((r) => (
                                            <tr key={r.client_id} className="border-t border-slate-100 reedos-row-hover" data-testid={`aging-client-row-${r.client_id}`}>
                                                <td className="px-5 py-3">
                                                    <Link to={`/clients/${r.client_id}/ledger`} className="font-semibold text-slate-900 hover:underline inline-flex items-center gap-1.5">
                                                        <Buildings size={12} className="text-slate-400" /> {r.client_name}
                                                    </Link>
                                                    <div className="text-[11px] text-slate-500 mt-0.5">
                                                        {r.state_code || "—"} · {r.count} invoice{r.count !== 1 ? "s" : ""}
                                                    </div>
                                                </td>
                                                <td className="px-5 py-3 text-right text-slate-500">{formatINR(r.current)}</td>
                                                <td className="px-5 py-3 text-right text-amber-700 reedos-data">{formatINR(r["0_30"])}</td>
                                                <td className="px-5 py-3 text-right text-orange-700 reedos-data">{formatINR(r["31_60"])}</td>
                                                <td className="px-5 py-3 text-right text-rose-700 reedos-data">{formatINR(r["61_90"])}</td>
                                                <td className="px-5 py-3 text-right text-rose-800 reedos-data font-semibold">{formatINR(r.over_90)}</td>
                                                <td className="px-5 py-3 text-right reedos-data font-semibold">{formatINR(r.outstanding)}</td>
                                                <td className="px-5 py-3 text-right reedos-data text-xs text-amber-700">{r.interest_accrued > 0 ? formatINR(r.interest_accrued) : "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Overdue invoices drilldown */}
                        <div className="reedos-card">
                            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                                <div>
                                    <div className="reedos-eyebrow">Overdue invoices</div>
                                    <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">{filteredOverdue.length} overdue</h3>
                                </div>
                            </div>
                            {filteredOverdue.length === 0 ? (
                                <EmptyState icon={<WarningOctagon size={36} weight="thin" />} title="No overdue invoices" description="All past-due invoices have been settled." />
                            ) : (
                                <table className="w-full text-sm" data-testid="aging-overdue-table">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr className="text-left">
                                            <th className="px-5 py-3 reedos-eyebrow">Invoice</th>
                                            <th className="px-5 py-3 reedos-eyebrow">Client</th>
                                            <th className="px-5 py-3 reedos-eyebrow">Due</th>
                                            <th className="px-5 py-3 reedos-eyebrow text-right">Days over</th>
                                            <th className="px-5 py-3 reedos-eyebrow">Bucket</th>
                                            <th className="px-5 py-3 reedos-eyebrow text-right">Outstanding</th>
                                            <th className="px-5 py-3 reedos-eyebrow text-right">Interest</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredOverdue.map((r) => {
                                            const meta = BUCKET_META.find((b) => b.key === r.bucket);
                                            return (
                                                <tr key={r.id} className="border-t border-slate-100 reedos-row-hover" data-testid={`aging-overdue-row-${r.id}`}>
                                                    <td className="px-5 py-3">
                                                        <Link to={`/invoices/${r.id}`} className="font-mono text-xs font-bold text-slate-900 hover:underline">{r.invoice_number}</Link>
                                                        <div className="text-[11px] text-slate-500 mt-0.5">{formatDate(r.invoice_date)}</div>
                                                    </td>
                                                    <td className="px-5 py-3">
                                                        <Link to={`/clients/${r.client_id}/ledger`} className="text-slate-800 hover:underline">{r.client_name}</Link>
                                                        <div className="text-[11px] text-slate-500 mt-0.5">{r.state_code}</div>
                                                    </td>
                                                    <td className="px-5 py-3 text-xs text-slate-600">{formatDate(r.due_date)}</td>
                                                    <td className="px-5 py-3 text-right text-xs text-rose-700 font-semibold">{r.days_overdue}d</td>
                                                    <td className="px-5 py-3">
                                                        <span className={classJoin("inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold border", meta?.tone || "bg-slate-50 border-slate-200")}>
                                                            {meta?.label || r.bucket}
                                                        </span>
                                                    </td>
                                                    <td className="px-5 py-3 text-right reedos-data font-semibold">{formatINR(r.outstanding)}</td>
                                                    <td className="px-5 py-3 text-right reedos-data text-xs text-amber-700">{r.interest_accrued > 0 ? formatINR(r.interest_accrued) : "—"}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
